import { Shelf } from "@/components/mobile/Shelf";
import { createFileRoute, Link } from "@tanstack/react-router";

import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  CalendarDays,
  Clock,
  Loader2,
  ExternalLink,
  MapPin,
  Sparkles,
  UserRound,
  Users,
  Video,
} from "lucide-react";

import { formatEventDate, formatEventTime, getCategory } from "@/lib/events";
import {
  EXTERNAL_DISCLAIMER,
  eventArtwork,
  eventArtworkAlt,
  eventPlace,
  isExternalEvent,
} from "@/lib/event-presentation";

import {
  listPublishedEvents,
  listEventsForMember,
  listMyReservations,
  type PublicEvent,
  type MemberEvent,
} from "@/lib/events.functions";
import { useMembership } from "@/hooks/useMembership";
import { getMonthlyTheme } from "@/lib/monthly-theme";
import { MonthlyThemeSection } from "@/components/events/MonthlyTheme";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import {
  EventAccessCta,
  MemberOnlyBadge,
  type EventViewerState,
} from "@/components/events/EventAccessCta";
import { track } from "@/lib/track";
import { startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";
import { useHideCommerce } from "@/lib/native-app";

type AnyEvent = PublicEvent | MemberEvent;

const SOURCE = "events_page";

export const Route = createFileRoute("/events")({
  head: () => ({
    meta: [
      { title: "Live Events & Workshops — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Live trauma-informed workshops, guided sessions and community experiences created for Recovery Trauma™ members.",
      },
      { property: "og:title", content: "Live Events & Workshops — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Live trauma-informed workshops, guided sessions and community experiences created for Recovery Trauma™ members.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/events" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/events" }],
  }),
  component: EventsPage,
});

function EventsPage() {
  const { isAuthed, isMember, isAdmin } = useMembership();
  const viewer: EventViewerState =
    isMember || isAdmin ? "member" : isAuthed ? "registered_unpaid" : "guest";

  const fetchPublic = useServerFn(listPublishedEvents);
  const fetchMember = useServerFn(listEventsForMember);
  const fetchReservations = useServerFn(listMyReservations);

  const eventsQuery = useQuery({
    queryKey: ["events", isAuthed ? "member" : "public"],
    queryFn: () => (isAuthed ? fetchMember() : fetchPublic()),
    retry: 1,
  });

  const reservationsQuery = useQuery({
    queryKey: ["my-reservations"],
    queryFn: () => fetchReservations(),
    enabled: isAuthed,
    retry: 1,
  });
  const reservedIds = useMemo(
    () => new Set(reservationsQuery.data ?? []),
    [reservationsQuery.data],
  );

  useEffect(() => {
    track("live_events_page_viewed", { viewer }, { source: SOURCE });
    // Viewer resolves asynchronously; one view event per resolved state is fine.
  }, [viewer]);

  const all = eventsQuery.data ?? [];
  const now = Date.now();

  const upcomingSlugs = useMemo(
    () =>
      all
        .filter((e) => +new Date(e.starts_at) >= now)
        .map((e) => e.slug)
        .filter((s): s is string => !!s),
    [all, now],
  );

  const theme = getMonthlyTheme(new Date(), upcomingSlugs);


  const { themeEvents, upcomingInternal, pastInternal, externalUpcoming } = useMemo(() => {
    const sorted = [...all].sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at));
    const internal = sorted.filter((e) => !isExternalEvent(e));
    const external = sorted.filter((e) => isExternalEvent(e));
    const upcoming = internal.filter((e) => +new Date(e.starts_at) >= now);
    const past = internal
      .filter((e) => +new Date(e.starts_at) < now)
      .sort((a, b) => +new Date(b.starts_at) - +new Date(a.starts_at));
    const slugs = theme?.eventSlugs ?? [];
    const inTheme = slugs
      .map((slug) => upcoming.find((e) => e.slug === slug))
      .filter((e): e is AnyEvent => !!e);
    const themeIds = new Set(inTheme.map((e) => e.id));
    return {
      themeEvents: inTheme,
      upcomingInternal: upcoming.filter((e) => !themeIds.has(e.id)),
      pastInternal: past,
      externalUpcoming: external.filter((e) => +new Date(e.starts_at) >= now),
    };
  }, [all, now, theme]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:py-12">
      {/* 1 — Introduction */}
      <header className="mb-8 text-center md:mb-12">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium text-foreground">
          <CalendarDays className="h-3.5 w-3.5 text-[var(--gold-deep)]" />
          Live Events
        </div>
        <h1 className="font-display text-3xl font-semibold text-foreground md:text-5xl">
          Live Events &amp; Workshops
        </h1>
        <p className="mx-auto mt-3 max-w-2xl font-display text-lg text-foreground md:text-xl">
          Connect, learn and recover together.
        </p>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Join live trauma-informed workshops, guided sessions and community experiences created
          especially for the Recovery Trauma™ community.
        </p>
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Recovery Trauma™ members can take part in live sessions covering different areas of
          recovery, self-understanding, creativity, emotional wellbeing and connection.
        </p>
        <div className="mt-5">
          <Link
            to="/workshop-signup"
            className="mr-2 inline-flex min-h-10 items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs font-semibold text-[var(--gold-deep)] hover:bg-gold/20"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Sign up: 20 September workshop
          </Link>
          <Link
            to="/events/calendar"
            className="inline-flex min-h-10 items-center gap-1.5 rounded-full border border-gold/40 bg-background px-4 py-1.5 text-xs font-semibold text-[var(--gold-deep)] hover:bg-gold/10"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            Open calendar view
          </Link>
        </div>
      </header>

      {eventsQuery.isLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
        </div>
      ) : (
        <>
          {/* 2 — Monthly recovery theme */}
          {theme ? (
            <section className="mb-12 md:mb-16">
              <MonthlyThemeSection theme={theme} />
            </section>
          ) : null}

          {/* 3 — This month's live workshops */}
          {themeEvents.length > 0 ? (
            <section aria-labelledby="this-month-heading" className="mb-14 md:mb-20">
              <div className="mb-6 max-w-2xl md:mb-8">
                <h2
                  id="this-month-heading"
                  className="font-display text-2xl text-foreground md:text-4xl"
                >
                  This Month's Live Workshops
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                  Each workshop explores a different part of this month's theme. Join the sessions
                  that feel useful to you, or experience the whole journey throughout the month.
                </p>
              </div>
              <Shelf cols={3} cardWidth="lg" className="mt-2">
                {themeEvents.map((e) => (
                  <InternalEventCard
                    key={e.id}
                    event={e}
                    viewer={viewer}
                    reserved={reservedIds.has(e.id)}
                  />
                ))}
              </Shelf>
            </section>
          ) : null}

          {/* 4 — Future Recovery Trauma™ events */}
          {upcomingInternal.length > 0 ? (
            <section aria-labelledby="rt-events-heading" className="mb-14 md:mb-20">
              <div className="mb-6 max-w-2xl md:mb-8">
                <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--gold-deep)]">
                  Recovery Trauma™ Programme
                </span>
                <h2
                  id="rt-events-heading"
                  className="mt-2 font-display text-2xl text-foreground md:text-3xl"
                >
                  Coming up next
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Live sessions already scheduled beyond this month's theme.
                </p>
              </div>
              <Shelf cols={3} cardWidth="lg" className="mt-2">
                {upcomingInternal.map((e) => (
                  <InternalEventCard
                    key={e.id}
                    event={e}
                    viewer={viewer}
                    reserved={reservedIds.has(e.id)}
                  />
                ))}
              </Shelf>
            </section>
          ) : null}

          {themeEvents.length === 0 && upcomingInternal.length === 0 ? (
            <div className="mb-14 rounded-3xl border border-gold/40 bg-cream/50 p-8 text-center md:mb-20 md:p-12 dark:bg-cream/10">
              <Sparkles className="mx-auto h-6 w-6 text-[var(--gold-deep)]" />
              <h3 className="mt-3 font-display text-xl text-foreground md:text-2xl">
                New live sessions are coming soon
              </h3>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
                We're preparing the next Recovery Trauma™ member workshops and live experiences.
              </p>
            </div>
          ) : null}

          {/* 7 — Membership benefit conversion block */}
          <MembershipBenefitBlock viewer={viewer} />

          {/* 11 — Past Recovery Trauma™ events */}
          {pastInternal.length > 0 ? (
            <section aria-labelledby="past-events-heading" className="mb-14 md:mb-20">
              <div className="mb-5 text-center">
                <h2
                  id="past-events-heading"
                  className="font-display text-xl text-foreground md:text-2xl"
                >
                  {viewer === "member"
                    ? "Past Recovery Trauma™ Events"
                    : "Previously at Recovery Trauma™"}
                </h2>
                <p className="mx-auto mt-2 max-w-xl text-xs text-muted-foreground sm:text-sm">
                  {viewer === "member"
                    ? "Sessions we've already run together."
                    : "A glimpse of the live sessions our members have already taken part in."}
                </p>
              </div>
              <Shelf cols={3} cardWidth="md" className="mt-2">
                {(viewer === "member" ? pastInternal : pastInternal.slice(0, 3)).map((e) => (
                  <PastEventCard key={e.id} event={e} viewer={viewer} />
                ))}
              </Shelf>
            </section>
          ) : null}

          {/* 8 — External events, secondary */}
          {externalUpcoming.length > 0 ? (
            <section aria-labelledby="external-events-heading" className="mb-10">
              <div className="mb-5 max-w-2xl">
                <h2
                  id="external-events-heading"
                  className="font-display text-lg text-foreground md:text-xl"
                >
                  Other Events You May Find Helpful
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                  A selection of external trauma-informed events, workshops and educational
                  sessions from other organisations that may support your recovery journey.
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  These events are not hosted by Recovery Trauma™ and may have separate
                  registration requirements, availability or costs.
                </p>
              </div>
              <Shelf cols={3} cardWidth="md" className="mt-2">
                {externalUpcoming.map((e) => (
                  <ExternalEventCard key={e.id} event={e} />
                ))}
              </Shelf>
              <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
                {EXTERNAL_DISCLAIMER}
              </p>
            </section>
          ) : null}
        </>
      )}

      <div className="mx-auto mt-10 max-w-2xl">
        <ComplianceNotice variant="events" />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- pieces */

function useImpression(event: AnyEvent, placement: string) {
  const fired = useRef(false);
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || fired.current || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired.current) {
            fired.current = true;
            track(
              "rt_event_impression",
              { slug: event.slug, title: event.title, placement },
              { source: SOURCE },
            );
            io.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [event.slug, event.title, placement]);
  return ref;
}

function CategoryPill({ category }: { category: string }) {
  const cat = getCategory(category);
  const label =
    cat.id === category ? cat.label : category.charAt(0).toUpperCase() + category.slice(1);
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-card/92 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground ring-1 ring-gold/40 backdrop-blur">
      {label}
    </span>
  );
}

function InternalEventCard({
  event,
  viewer,
  reserved,
}: {
  event: AnyEvent;
  viewer: EventViewerState;
  reserved: boolean;
}) {
  const ref = useImpression(event, "grid");
  return (
    <article
      ref={ref as React.RefObject<HTMLElement>}
      className="flex h-full flex-col overflow-hidden rounded-3xl border border-gold/40 bg-card shadow-[var(--shadow-gold)]"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden sm:aspect-[16/10]">
        <img
          src={eventArtwork(event)}
          alt={eventArtworkAlt(event)}
          loading="lazy"
          decoding="async"
          width={1280}
          height={800}
          className="h-full w-full object-cover object-[50%_38%]"
        />
        <span className="absolute start-3 top-3">
          <CategoryPill category={event.category} />
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <MemberOnlyBadge className="self-start" />
        <h3 className="mt-3 font-display text-xl text-foreground sm:text-2xl">{event.title}</h3>
        <p className="mt-1.5 font-display text-sm text-[var(--gold-deep)] sm:text-base">
          {formatEventDate(event.starts_at)} · {formatEventTime(event.starts_at)}
        </p>
        <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground sm:text-sm">
          <UserRound className="h-3.5 w-3.5" /> Hosted by {event.host}
        </p>

        {event.short_description || event.description ? (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            {event.short_description || event.description}
          </p>
        ) : null}

        <Link
          to="/events/$slug"
          params={{ slug: event.slug }}
          className="mt-3 inline-flex w-fit items-center gap-1 text-sm font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline"
        >
          Read more
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>

        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {event.duration_minutes} min
          </span>
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" /> {eventPlace(event)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {event.reservation_count === 1
              ? "1 place reserved"
              : `${event.reservation_count} places reserved`}
            {event.registration_limit ? ` of ${event.registration_limit}` : ""}
          </span>
        </div>

        <div className="mt-auto pt-5">
          <EventAccessCta
            viewer={viewer}
            slug={event.slug}
            eventTitle={event.title}
            reserved={reserved}
            source={`${SOURCE}_grid`}
          />
        </div>
      </div>
    </article>
  );
}

function PastEventCard({ event, viewer }: { event: AnyEvent; viewer: EventViewerState }) {
  const hasRecording = false; // Recordings are member-only and not currently published.
  return (
    <article className="flex gap-3 overflow-hidden rounded-2xl border border-border/70 bg-card/80 p-3">
      <img
        src={eventArtwork(event)}
        alt={eventArtworkAlt(event)}
        loading="lazy"
        decoding="async"
        width={160}
        height={160}
        className="h-20 w-20 shrink-0 rounded-xl object-cover object-[50%_35%]"
      />
      <div className="min-w-0">
        <h3 className="truncate font-display text-sm text-foreground">{event.title}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {formatEventDate(event.starts_at)}
        </p>
        {viewer === "member" ? (
          hasRecording ? (
            <Link
              to="/events/$slug"
              params={{ slug: event.slug }}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline"
            >
              <Video className="h-3.5 w-3.5" /> Watch Recording
            </Link>
          ) : (
            <p className="mt-2 text-[11px] font-medium text-muted-foreground">Event Completed</p>
          )
        ) : (
          <p className="mt-2 text-[11px] font-medium text-muted-foreground">Members only</p>
        )}
      </div>
    </article>
  );
}

function ExternalEventCard({ event }: { event: AnyEvent }) {
  const href = event.external_url ?? undefined;
  return (
    <article className="flex gap-3 rounded-2xl border border-border/70 bg-card/70 p-3">
      <img
        src={eventArtwork(event)}
        alt={eventArtworkAlt(event)}
        loading="lazy"
        decoding="async"
        width={128}
        height={128}
        className="h-16 w-16 shrink-0 rounded-lg object-cover object-[50%_35%] opacity-90"
      />
      <div className="min-w-0 flex-1">
        <h3 className="line-clamp-2 text-sm font-semibold text-foreground">{event.title}</h3>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{event.host}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {formatEventDate(event.starts_at)} · {formatEventTime(event.starts_at)}
        </p>
        <span className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          External Event
        </span>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={() =>
              track(
                "external_event_clicked",
                { slug: event.slug, title: event.title, host: event.host },
                { source: SOURCE },
              )
            }
            className="mt-2 flex min-h-[36px] items-center gap-1 text-xs font-semibold text-primary underline-offset-4 hover:underline"
          >
            View External Event <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

function MembershipBenefitBlock({ viewer }: { viewer: EventViewerState }) {
  const hideCommerce = useHideCommerce();
  if (viewer === "member") return null;
  return (
    <section className="mb-14 rounded-3xl border border-gold/40 bg-cream/50 p-6 text-center sm:p-10 md:mb-20 dark:bg-cream/10">
      <h2 className="font-display text-xl text-foreground sm:text-3xl">
        More than a library of resources.
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-sm text-foreground/80 sm:text-base">
        Recovery doesn't always have to happen alone.
      </p>
      <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Your Recovery Trauma™ membership includes opportunities to take part in live workshops,
        guided sessions and community experiences throughout the month — alongside the tools,
        education and resources available inside the platform.
      </p>
      <div className="mt-6">
        <Link
          to={hideCommerce ? "/app-membership" : MEMBERSHIP_JOURNEY_PATH}
          onClick={() => {
            track(
              "event_cta_clicked",
              { cta: "events_membership_block" },
              { source: SOURCE },
            );
            startMembershipJourney("events_membership_block", SOURCE);
          }}
          className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-8 font-display text-base font-medium text-foreground shadow-[var(--shadow-gold)] transition-all duration-300 hover:-translate-y-0.5 sm:w-auto"
        >
          Join Recovery Trauma™
        </Link>
        {!hideCommerce && (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">$14.99 / month</span> · Cancel anytime · Immediate access
          </p>
        )}
      </div>
    </section>
  );
}
